package co.uk.paxes.pinpoint.web

import co.uk.paxes.pinpoint.model.*
import kotlinx.browser.window
import kotlin.js.Date

/**
 * InternetGameManager handles internet multiplayer games using the SharedBoardManager.
 * 
 * This is a clean implementation that:
 * - Uses Firebase as the single source of truth
 * - Renders board directly from shared state
 * - Handles moves, undo, and turn management
 * - Calculates scores locally from board state
 */
object InternetGameManager {
    
    // Version for debugging - increment when making changes
    private const val VERSION = "2026-03-08-v9-ai-unification"
    
    // Score calculator instance
    private val scoreCalculator = ScoreCalculator()
    
    // Game state (derived from Firebase)
    private var boardPins: Map<String, SharedBoardManager.SharedPin> = emptyMap()
    private var gameMeta: SharedBoardManager.GameMeta? = null
    private var players: Map<String, SharedBoardManager.SharedPlayer> = emptyMap()
    
    // Local state
    private var selectedPinIndex: Int = 0
    private var isInHandPause: Boolean = false
    private var handPauseTimeoutId: Int? = null
    private var isAIMoveScheduled: Boolean = false
    
    // Undo tracking - stack of moves made this hand (for multiple undos)
    private data class UndoableMove(
        val position: String,  // "row_col"
        val reversedPositions: List<String>,  // Positions that were reversed by this move
        val reversedOriginalColors: Map<String, String>,  // Original colors of reversed pins (posKey -> color)
        val pinColor: String,  // Color to restore to hand (handColor for jokers, color for regular)
        val isJoker: Boolean
    )
    private val moveStack: MutableList<UndoableMove> = mutableListOf()
    
    // Legacy single-move tracking (kept for compatibility, will be removed)
    private var lastMovePosition: String? = null
    
    // Score tracking for display
    private var lastPinScore: Int = 0  // Score from the last pin placed
    private var currentHandScore: Int = 0  // Accumulated score for current hand (resets when turn passes)
    
    // Scoring positions - pins that contributed to the last score (for white ring highlights)
    private var scoringPositions: Set<String> = emptySet()  // Set of "row_col" position keys
    
    // Track pins that were reversed by the last move (for undo)
    private var lastMoveReversedPositions: List<String> = emptyList()  // List of "row_col" position keys
    
    // Track jokers that were turned black when hand completed (for undo during pause)
    // Maps position key to original color
    private var blackenedJokers: MutableMap<String, String> = mutableMapOf()
    
    // Callbacks
    var onBoardUpdated: (() -> Unit)? = null
    var onTurnChanged: ((String) -> Unit)? = null
    var onPinPlaced: (() -> Unit)? = null  // New callback for when a pin is placed
    var onGamePhaseChanged: ((String) -> Unit)? = null
    var onGameEnded: ((Map<String, Int>, String?) -> Unit)? = null
    var onScoringPositionsChanged: ((List<String>) -> Unit)? = null  // For flash animation
    var onError: ((String) -> Unit)? = null
    
    // ========================================
    // Initialization
    // ========================================
    
    /**
     * Start a new internet multiplayer game.
     * Called when navigating to the game screen.
     */
    fun startGame(
        roomId: String,
        playerId: String,
        playerName: String,
        isHost: Boolean,
        allPlayers: List<SharedBoardManager.PlayerInitInfo>,  // Full player metadata
        maxUndosPerHand: Int = 1  // Default 1 undo per hand
    ) {
        console.log("🎮🎮🎮 InternetGameManager VERSION: $VERSION 🎮🎮🎮")
        console.log("InternetGameManager: Starting game - room=$roomId, player=$playerId, isHost=$isHost, maxUndos=$maxUndosPerHand")
        
        // Reset local state
        boardPins = emptyMap()
        gameMeta = null
        players = emptyMap()
        selectedPinIndex = 0
        isInHandPause = false
        lastMovePosition = null
        moveStack.clear()
        blackenedJokers.clear()
        
        // Initialize AI players based on the player list
        // initializeAIPlayers(allPlayers)
        
        // Set up SharedBoardManager callbacks
        SharedBoardManager.onBoardChanged = { pins ->
            console.log("InternetGameManager: 📥 onBoardChanged received - ${pins.size} pins")
            
            // Detect new pins added (for remote player visual/audio feedback)
            val newPins = pins.keys - boardPins.keys
            if (newPins.isNotEmpty()) {
                val myPlayerId = SharedBoardManager.getCurrentPlayerId()
                val hasRemotePin = newPins.any { pos -> pins[pos]?.playerId != myPlayerId }
                if (hasRemotePin) {
                    console.log("InternetGameManager: 🔊 Remote pin detected, playing sound")
                    SoundManager.playPinPlaced()
                }
            }
            
            val prevBoard = boardPins
            boardPins = pins
            
            // Only trigger if pins actually changed
            if (pins != prevBoard) {
                console.log("InternetGameManager: Board updated - ${pins.size} pins")
                onBoardUpdated?.invoke()
            }
            checkForGameEnd()
        }
        
        SharedBoardManager.onMetaChanged = { meta ->
            val previousPhase = gameMeta?.gamePhase
            val previousPlayer = gameMeta?.currentPlayerId
            // CRITICAL: Use gameMeta's scoringPositions, not the local variable
            // This ensures we detect changes even for the player who made the move
            val previousScoringPositions = gameMeta?.scoringPositions ?: emptyList()
            val previousLastPinScore = gameMeta?.lastPinScore ?: 0
            
            // Store previous meta before updating
            val prevMeta = gameMeta
            gameMeta = meta
            
            console.log("InternetGameManager: Meta changed - phase=${meta.gamePhase}, player=${meta.currentPlayerId}, pinScore=${meta.lastPinScore}")
            
            // Check if scoring positions changed (trigger flash AND sound)
            val currentPositions = meta.scoringPositions
            val prevPositions = previousScoringPositions
            
            val positionsChanged = currentPositions.toSet() != prevPositions.toSet()
            val hasPositions = currentPositions.isNotEmpty()
            val scoreChanged = meta.lastPinScore != previousLastPinScore
            
            console.log("InternetGameManager: ⚡ check - current: $currentPositions, prev: $prevPositions, changed: $positionsChanged, scoreChanged: $scoreChanged, has: $hasPositions")
            
            // DEBUG: Special logging for 1-point scores
            if (meta.lastPinScore == 1) {
                console.log("🐛 DEBUG 1-POINT SCORE: positions=$currentPositions, hasPositions=$hasPositions, will flash=${hasPositions}")
            }
            
            // Only flash/sound if something changed OR it's a new turn
            val isNewTurn = previousPlayer != meta.currentPlayerId
            if (positionsChanged || scoreChanged || isNewTurn) {
                if (hasPositions) {
                    console.log("InternetGameManager: ⚡ Scoring positions present: ${currentPositions.size} positions - Triggering callback")
                    onScoringPositionsChanged?.invoke(currentPositions)
                } else if (isNewTurn) {
                    // Clear scoring highlights on new turn
                    onScoringPositionsChanged?.invoke(emptyList())
                }
                
                // Play score sound if score increased
                if (scoreChanged && meta.lastPinScore > 0) {
                    console.log("InternetGameManager: 🔊 Playing score sound: ${meta.lastPinScore} points")
                    if (SettingsManager.isSoundEnabled()) {
                        SoundManager.playScoreSound(meta.lastPinScore)
                    }
                }
            }
            
            // If phase changed from PAUSED to PLAYING, clear any local timer
            // (another client may have advanced the turn via fallback)
            if (previousPhase == "PAUSED" && meta.gamePhase == "PLAYING") {
                if (handPauseTimeoutId != null) {
                    console.log("InternetGameManager: Clearing local timer - turn was advanced by another client")
                    window.clearTimeout(handPauseTimeoutId!!)
                    handPauseTimeoutId = null
                }
                isInHandPause = false
            }
            
            // Notify of phase changes
            if (previousPhase != meta.gamePhase) {
                console.log("InternetGameManager: 📢 Phase changed: $previousPhase -> ${meta.gamePhase}")
                onGamePhaseChanged?.invoke(meta.gamePhase)
                
                if (meta.gamePhase == "PAUSED") {
                    console.log("InternetGameManager: 📢 Calling handleHandPause due to phase change")
                    handleHandPause()
                } else if (meta.gamePhase == "PLAYING") {
                    // Clear pause flag when game resumes
                    isInHandPause = false
                }
            } else {
                // Phase didn't change, but if we're PAUSED and HOST, we might need to handle it
                // This catches cases where HOST receives multiple PAUSED updates
                if (meta.gamePhase == "PAUSED") {
                    val isHost = SharedBoardManager.isCurrentPlayerHost()
                    val myPlayerId = SharedBoardManager.getCurrentPlayerId()
                    console.log("InternetGameManager: 📢 Phase still PAUSED, isHost=$isHost, handPauseTimeoutId=$handPauseTimeoutId, pauseStartedBy=${meta.pauseStartedBy}")
                    
                    // If HOST and no timer running, handle it (allows recovery after refresh)
                    if (isHost && handPauseTimeoutId == null) {
                        console.log("InternetGameManager: 📢 HOST with no timer - calling handleHandPause for recovery")
                        handleHandPause()
                    }
                }
            }
            
            // Notify of turn changes
            if (previousPlayer != meta.currentPlayerId) {
                console.log("InternetGameManager: 🔄 Turn changed from $previousPlayer to ${meta.currentPlayerId}")
                onTurnChanged?.invoke(meta.currentPlayerId)
            }
            
            // Check if it's an AI player's turn and handle AI move
            // We call this on every meta update while it's an AI's turn 
            // to ensure they complete their 4-pin hand.
            handleAITurnIfNeeded(meta.currentPlayerId)
            
            onBoardUpdated?.invoke()
        }
        
        SharedBoardManager.onPlayersChanged = { playerMap ->
            val prevPlayers = players
            players = playerMap
            
            if (playerMap != prevPlayers) {
                console.log("InternetGameManager: Players updated - ${playerMap.size} players total")
                onBoardUpdated?.invoke()
            }
        }
        
        SharedBoardManager.onError = { error ->
            console.error("InternetGameManager: SharedBoardManager error: $error")
            onError?.invoke(error)
        }
        
        // Initialize SharedBoardManager
        SharedBoardManager.initialize(roomId, playerId, isHost)
        
        // If host, initialize the game state in Firebase
        if (isHost) {
            val gameSettings = SharedBoardManager.GameSettings(maxUndosPerHand = maxUndosPerHand)
            SharedBoardManager.initializeGame(roomId, allPlayers, playerId, gameSettings) { success, error ->
                if (success) {
                    console.log("InternetGameManager: Game initialized successfully with maxUndos=$maxUndosPerHand")
                } else {
                    console.error("InternetGameManager: Failed to initialize game: $error")
                    onError?.invoke(error ?: "Failed to initialize game")
                }
            }
        }
    }
    
    /**
     * Clean up when leaving the game.
     */
    fun cleanup() {
        console.log("InternetGameManager: Cleaning up")
        
        handPauseTimeoutId?.let { window.clearTimeout(it) }
        handPauseTimeoutId = null
        
        SharedBoardManager.cleanup()
        
        boardPins = emptyMap()
        gameMeta = null
        players = emptyMap()
    }
    
    // ========================================
    // Game Actions
    // ========================================
    
    /**
     * Make a move - place a pin on the board.
     * After placing, turn passes to the other player.
     */
    fun makeMove(row: Int, col: Int, forPlayerId: String? = null): Boolean {
        val meta = gameMeta ?: run {
            console.log("InternetGameManager.makeMove: ❌ No game meta!")
            return false
        }
        
        console.log("InternetGameManager.makeMove: Attempting move at ($row, $col) forPlayerId=$forPlayerId")
        val playerId = forPlayerId ?: SharedBoardManager.getCurrentPlayerId() ?: run {
            console.log("InternetGameManager.makeMove: ❌ No player ID!")
            return false
        }
        console.log("InternetGameManager.makeMove: My ID=$playerId, Current turn=${meta.currentPlayerId}, Phase=${meta.gamePhase}")
        
        // Check if it's our turn
        if (meta.currentPlayerId != playerId) {
            val errorMsg = "❌ Not our turn! (Firebase thinks: ${meta.currentPlayerId}, local ID: $playerId)"
            console.error(errorMsg)
            // Optionally show a small toast or temporary message
            return false
        }
        
        // Check if game is paused
        if (meta.gamePhase == "PAUSED") {
            console.log("InternetGameManager.makeMove: ❌ Game is paused")
            return false
        }
        
        // Check if position is empty
        val posKey = "${row}_${col}"
        if (boardPins.containsKey(posKey)) {
            console.log("InternetGameManager: Position $posKey is occupied")
            return false
        }
        
        // Get the selected pin from our hand
        val player = players[playerId] ?: return false
        if (selectedPinIndex >= player.hand.size) {
            console.log("InternetGameManager: Invalid pin selection")
            return false
        }
        
        val pinColorName = player.hand[selectedPinIndex]
        val pinColor = try { PinColor.valueOf(pinColorName) } catch (_: Exception) { PinColor.RED }
        val pin = Pin.regular(pinColor)
        
        // Forcibly wake all audio systems synchronously (Nuclear Fix Phase 5)
        SoundManager.joltAudio()
        
        // Clear previous move's reversed positions (we only track the most recent move)
        lastMoveReversedPositions = emptyList()
        
        // Play placement sound synchronously BEFORE async network call
        SoundManager.playPinPlaced()
        
        // Place the pin
        SharedBoardManager.placePin(row, col, pin, playerId) { success, error ->
            if (success) {
                lastMovePosition = posKey
                
                // Notify that a pin was placed
                onPinPlaced?.invoke()
                
                // Push move onto stack for undo (reversedPositions will be updated after scoring)
                moveStack.add(UndoableMove(
                    position = posKey,
                    reversedPositions = emptyList(),  // Updated after scoring
                    reversedOriginalColors = emptyMap(),  // Updated after scoring
                    pinColor = pinColorName,
                    isJoker = false
                ))
                console.log("InternetGameManager: 📚 Pushed move to stack, size=${moveStack.size}")
                
                // Remove pin from hand
                val newHand = player.hand.toMutableList()
                newHand.removeAt(selectedPinIndex)
                
                // Update player's hand in Firebase - use JS array for compatibility
                val handArray = js("[]")
                newHand.forEachIndexed { i, c -> handArray[i] = c }
                SharedBoardManager.updatePlayer(playerId, mapOf("hand" to handArray))
                
                // Reset selection
                selectedPinIndex = 0
                
                // Calculate and update score
                updateScoreAfterMove(playerId, row, col, pin)
                
                // Only pass turn when hand is empty (all 4 pins placed)
                if (newHand.isEmpty()) {
                    console.log("InternetGameManager: Hand empty - passing turn to next player")
                    passTurnToNextPlayer()
                } else {
                    console.log("InternetGameManager: ${newHand.size} pins remaining in hand")
                }
            } else {
                console.error("InternetGameManager: Failed to place pin: $error")
                onError?.invoke(error ?: "Failed to place pin")
            }
        }
        
        return true
    }
    
    /**
     * Make a move with a joker - place a pin that acts as a different color.
     * The joker will be stored with its original color and the chosen color.
     */
    fun makeMoveWithJoker(row: Int, col: Int, jokerColor: String, forPlayerId: String? = null): Boolean {
        val meta = gameMeta ?: return false
        val playerId = forPlayerId ?: SharedBoardManager.getCurrentPlayerId() ?: return false
        
        // Check if it's our turn
        if (meta.currentPlayerId != playerId) {
            console.log("InternetGameManager: Not our turn")
            return false
        }
        
        // Check if game is paused
        if (meta.gamePhase == "PAUSED") {
            console.log("InternetGameManager: Game is paused")
            return false
        }
        
        // Check if position is empty
        val posKey = "${row}_${col}"
        if (boardPins.containsKey(posKey)) {
            console.log("InternetGameManager: Position $posKey is occupied")
            return false
        }
        
        // Get the selected pin from our hand (the one being sacrificed for joker)
        val player = players[playerId] ?: return false
        if (selectedPinIndex >= player.hand.size) {
            console.log("InternetGameManager: Invalid pin selection")
            return false
        }
        
        // Check if player can still use jokers (max 4 per game)
        if (player.jokersUsed >= 4) {
            console.log("InternetGameManager: ❌ Maximum jokers used (${player.jokersUsed}/4)")
            onError?.invoke("You have already used the maximum number of jokers (4) this game!")
            return false
        }
        
        val originalColorName = player.hand[selectedPinIndex]
        
        // Forcibly wake all audio systems synchronously (Nuclear Fix Phase 5)
        SoundManager.joltAudio()
        
        // Clear previous move's reversed positions (we only track the most recent move)
        lastMoveReversedPositions = emptyList()
        
        // Play joker creation sound
        if (SettingsManager.isSoundEnabled()) {
            SoundManager.playJokerCreated()
        }
        
        // Play placement sound synchronously BEFORE async network call
        SoundManager.playPinPlaced()
        
        // Place the joker pin - show the SELECTED joker color with J indicator
        // It will turn black when the turn passes
        SharedBoardManager.placePin(
            row = row,
            col = col,
            color = jokerColor,  // Show the selected joker color (will turn black after hand)
            isJoker = true,
            originalColor = jokerColor,  // The color it counts as for scoring
            handColor = originalColorName,  // The original hand color (for undo)
            forPlayerId = playerId  // Specify which player is placing the joker
        ) { success, error ->
            if (success) {
                lastMovePosition = posKey
                
                // Notify that a pin was placed
                onPinPlaced?.invoke()
                
                // Push move onto stack for undo (reversedPositions will be updated after scoring)
                moveStack.add(UndoableMove(
                    position = posKey,
                    reversedPositions = emptyList(),  // Updated after scoring
                    reversedOriginalColors = emptyMap(),  // Updated after scoring
                    pinColor = originalColorName,  // Restore original hand color on undo
                    isJoker = true
                ))
                console.log("InternetGameManager: 📚 Pushed joker move to stack, size=${moveStack.size}")
                
                // Remove pin from hand
                val newHand = player.hand.toMutableList()
                newHand.removeAt(selectedPinIndex)
                
                // Update player's hand in Firebase (jokersUsed is incremented by SharedBoardManager.placePin)
                val handArray = js("[]")
                newHand.forEachIndexed { i, c -> handArray[i] = c }
                SharedBoardManager.updatePlayer(playerId, mapOf(
                    "hand" to handArray
                ))
                
                // Reset selection
                selectedPinIndex = 0
                
                // Calculate and update score (using the joker color for scoring)
                val jokerPinColor = try { PinColor.valueOf(jokerColor) } catch (_: Exception) { PinColor.RED }
                val jokerPin = Pin.joker(PinColor.BLACK, jokerPinColor)
                updateScoreAfterMove(playerId, row, col, jokerPin)
                
                // Only pass turn when hand is empty (all 4 pins placed)
                if (newHand.isEmpty()) {
                    console.log("InternetGameManager: Hand empty - passing turn to next player")
                    passTurnToNextPlayer()
                } else {
                    console.log("InternetGameManager: ${newHand.size} pins remaining in hand")
                }
            } else {
                console.error("InternetGameManager: Failed to place joker: $error")
                onError?.invoke(error ?: "Failed to place joker")
            }
        }
        
        return true
    }
    
    /**
     * Start the 3-second hand completion pause.
     * This gives the player time to undo their last move before turn passes.
     * 
     * CRITICAL: Only HOST sets timers. Clients just set PAUSED phase and wait.
     */
    private fun startHandCompletionPause() {
        val myPlayerId = SharedBoardManager.getCurrentPlayerId()
        val isHost = SharedBoardManager.isCurrentPlayerHost()
        
        console.log("InternetGameManager: 🕐🕐🕐 Starting 3-second hand completion pause 🕐🕐🕐")
        console.log("InternetGameManager: I am ${if (isHost) "HOST" else "CLIENT"}, myPlayerId=$myPlayerId")
        console.log("InternetGameManager: Current handPauseTimeoutId before: $handPauseTimeoutId")
        
        // If we already have a timer running, don't start another pause
        if (handPauseTimeoutId != null) {
            console.log("InternetGameManager: ⚠️ Timer already running, skipping startHandCompletionPause")
            return
        }
        
        isInHandPause = true
        
        // Calculate when the pause should end (3 seconds from now)
        val pauseEndTime = Date.now() + 3000
        
        console.log("InternetGameManager: Setting phase to PAUSED, pauseEndTime=$pauseEndTime, pauseStartedBy=$myPlayerId")
        
        // Set game phase to PAUSED in Firebase so all clients know
        SharedBoardManager.updateMeta(mapOf(
            "gamePhase" to "PAUSED",
            "pauseEndTime" to pauseEndTime,
            "pauseStartedBy" to myPlayerId
        ))
        
        // Notify UI of phase change
        onGamePhaseChanged?.invoke("PAUSED")
        
        // CRITICAL: Only the HOST should set a timer to advance the turn
        if (isHost) {
            console.log("InternetGameManager: 🏠 I am HOST - Setting timer to advance turn in 3 seconds")
            handPauseTimeoutId = window.setTimeout({
                console.log("InternetGameManager: ⏰⏰⏰ HOST TIMER FIRED! Pause ended ⏰⏰⏰")
                console.log("InternetGameManager: ⏰ Current gameMeta.gamePhase: ${gameMeta?.gamePhase}")
                console.log("InternetGameManager: ⏰ Current gameMeta.currentPlayerId: ${gameMeta?.currentPlayerId}")
                isInHandPause = false
                handPauseTimeoutId = null
                actuallyPassTurnToNextPlayer()
            }, 3000)
            console.log("InternetGameManager: Host timer started with ID: $handPauseTimeoutId")
        } else {
            console.log("InternetGameManager: 👤 I am CLIENT - Not setting timer, HOST will handle turn advance")
            // CLIENT doesn't set timer - HOST will detect PAUSED phase and handle it
        }
    }
    
    /**
     * Cancel the hand completion pause (called when undo is used).
     */
    private fun cancelHandCompletionPause() {
        if (handPauseTimeoutId != null) {
            console.log("InternetGameManager: Cancelling hand completion pause")
            window.clearTimeout(handPauseTimeoutId!!)
            handPauseTimeoutId = null
            isInHandPause = false
            
            // Set game phase back to PLAYING and clear pause fields
            // This tells other clients to cancel their fallback timers too
            SharedBoardManager.updateMeta(mapOf(
                "gamePhase" to "PLAYING",
                "pauseEndTime" to null,
                "pauseStartedBy" to null
            ))
            onGamePhaseChanged?.invoke("PLAYING")
        } else if (isInHandPause) {
            // We're observing a pause started by another client - still need to cancel
            console.log("InternetGameManager: Cancelling observed hand completion pause")
            isInHandPause = false
            
            SharedBoardManager.updateMeta(mapOf(
                "gamePhase" to "PLAYING",
                "pauseEndTime" to null,
                "pauseStartedBy" to null
            ))
            onGamePhaseChanged?.invoke("PLAYING")
        }
    }
    
    /**
     * Pass turn to the next player (called when hand is empty).
     * Now starts a 3-second pause first to allow undo.
     */
    private fun passTurnToNextPlayer() {
        // Turn any jokers placed this hand to black before starting pause
        turnJokersBlack()
        
        // Start the pause - actual turn pass happens after 3 seconds
        startHandCompletionPause()
    }
    
    /**
     * Turn all jokers placed by the current player to black.
     * Called when the hand ends (all 4 pins placed).
     * Tracks which jokers were blackened so they can be restored on undo.
     */
    private fun turnJokersBlack() {
        // Use the current turn's player ID, not just the local player ID
        val playerId = gameMeta?.currentPlayerId ?: SharedBoardManager.getCurrentPlayerId() ?: return
        
        // Clear previous tracking
        blackenedJokers.clear()
        
        // Find all jokers placed by this player that aren't already black
        val jokersToBlacken = boardPins.filter { (_, pin) ->
            pin.isJoker && pin.playerId == playerId && pin.color != "BLACK"
        }
        
        if (jokersToBlacken.isEmpty()) {
            console.log("InternetGameManager: No jokers to turn black")
            return
        }
        
        console.log("InternetGameManager: 🖤 Turning ${jokersToBlacken.size} jokers black")
        
        // Update each joker to black and track original color
        for ((posKey, pin) in jokersToBlacken) {
            // Track the original color so we can restore it on undo
            blackenedJokers[posKey] = pin.color
            
            val parts = posKey.split("_")
            val row = parts[0].toIntOrNull() ?: continue
            val col = parts[1].toIntOrNull() ?: continue
            
            SharedBoardManager.updatePin(row, col, mapOf("color" to "BLACK")) { success, error ->
                if (success) {
                    console.log("InternetGameManager: ✅ Joker at $posKey turned black (was ${pin.color})")
                } else {
                    console.error("InternetGameManager: ❌ Failed to turn joker black at $posKey: $error")
                }
            }
        }
    }
    
    /**
     * Restore jokers that were turned black back to their original colors.
     * Called when undoing during the hand completion pause.
     * @param excludePosition Position to exclude from restoration (the pin being undone)
     */
    private fun restoreBlackenedJokers(excludePosition: String? = null) {
        if (blackenedJokers.isEmpty()) {
            console.log("InternetGameManager: No blackened jokers to restore")
            return
        }
        
        // Filter out the excluded position
        val jokersToRestore = if (excludePosition != null) {
            blackenedJokers.filterKeys { it != excludePosition }
        } else {
            blackenedJokers.toMap()
        }
        
        if (jokersToRestore.isEmpty()) {
            console.log("InternetGameManager: No blackened jokers to restore (after exclusion)")
            blackenedJokers.clear()
            return
        }
        
        console.log("InternetGameManager: 🎨 Restoring ${jokersToRestore.size} blackened jokers to original colors (excluded: $excludePosition)")
        
        for ((posKey, originalColor) in jokersToRestore) {
            val parts = posKey.split("_")
            val row = parts[0].toIntOrNull() ?: continue
            val col = parts[1].toIntOrNull() ?: continue
            
            SharedBoardManager.updatePin(row, col, mapOf("color" to originalColor)) { success, error ->
                if (success) {
                    console.log("InternetGameManager: ✅ Joker at $posKey restored to $originalColor")
                } else {
                    console.error("InternetGameManager: ❌ Failed to restore joker at $posKey: $error")
                }
            }
        }
        
        // Clear the tracking after restoration
        blackenedJokers.clear()
    }
    
    /**
     * Actually pass turn to the next player (called after pause ends).
     * Deals a fresh hand of 4 pins to the next player.
     * CRITICAL: Only the HOST should call this to prevent duplicate turn passes.
     */
    private fun actuallyPassTurnToNextPlayer() {
        console.log("InternetGameManager: 🔄 actuallyPassTurnToNextPlayer() called")
        val meta = gameMeta
        if (meta == null) {
            console.error("InternetGameManager: ❌ actuallyPassTurnToNextPlayer - meta is null!")
            return
        }
        
        // CRITICAL: Only HOST should pass turns to prevent duplicate updates
        val isHost = SharedBoardManager.isCurrentPlayerHost()
        if (!isHost) {
            console.log("InternetGameManager: ⚠️ actuallyPassTurnToNextPlayer - I am NOT HOST, skipping (HOST will handle)")
            return
        }
        
        console.log("InternetGameManager: 🏠 actuallyPassTurnToNextPlayer - I am HOST, proceeding")
        
        // Guard: Don't pass turn if game is already PLAYING (turn was already passed)
        if (meta.gamePhase == "PLAYING") {
            console.log("InternetGameManager: ⚠️ actuallyPassTurnToNextPlayer - game already PLAYING, skipping")
            return
        }
        
        // Sort players so host is always first (consistent with Main.kt initialization)
        val playerList = players.entries.sortedWith(
            compareByDescending<Map.Entry<String, SharedBoardManager.SharedPlayer>> { it.value.isHost }
            .thenBy { it.key }
        ).map { it.key }
        
        console.log("InternetGameManager: 🔄 actuallyPassTurnToNextPlayer - Sorted player IDs (Host First):", playerList.toTypedArray())
        console.log("InternetGameManager: 🔄 Current player ID from meta: ${meta.currentPlayerId}")
        
        // CRITICAL: Re-fetch meta from Firebase to get the latest state
        // This prevents race conditions where multiple clients try to pass the turn
        val latestMeta = gameMeta
        if (latestMeta != null && latestMeta.gamePhase == "PLAYING") {
            console.log("InternetGameManager: ⚠️ actuallyPassTurnToNextPlayer - latest meta shows PLAYING, aborting")
            return
        }
        
        val rawPlayerMap = players.entries.associate { (id, player) -> 
            id to "Name: ${player.name}, Host: ${player.isHost}"
        }
        console.log("InternetGameManager: 🔄 Current state of players map:", JSON.stringify(rawPlayerMap, null, 2))
        
        if (playerList.isEmpty()) {
            console.error("InternetGameManager: ❌ actuallyPassTurnToNextPlayer - playerList is empty!")
            return
        }
        
        if (playerList.size == 1) {
            console.log("InternetGameManager: Single player game - staying on same player")
            // Single player - just deal a new hand to the same player
            val singlePlayerId = playerList[0]
            val handColors = listOf("RED", "GREEN", "PURPLE", "YELLOW")
            val newHand = handColors.shuffled()
            
            // Create JS array for Firebase compatibility
            val handArray = js("[]")
            newHand.forEachIndexed { i, c -> handArray[i] = c }
            
            SharedBoardManager.updatePlayer(singlePlayerId, mapOf("hand" to handArray)) { success, error ->
                if (success) {
                    SharedBoardManager.updateMeta(mapOf(
                        "handCount" to (meta.handCount + 1),
                        "currentHandScore" to 0,
                        "gamePhase" to "PLAYING",
                        "pauseEndTime" to null,
                        "pauseStartedBy" to null
                    ))
                    onGamePhaseChanged?.invoke("PLAYING")
                }
            }
            return
        }
        
        val currentIndex = playerList.indexOf(meta.currentPlayerId)
        console.log("InternetGameManager: 🔄 Current index in sorted list: $currentIndex")
        
        if (currentIndex == -1) {
            console.error("InternetGameManager: ❌ Current player ${meta.currentPlayerId} not found in playerList: $playerList")
            // Fallback: just go to first player
        }
        
        val nextIndex = if (currentIndex == -1) 0 else (currentIndex + 1) % playerList.size
        val nextPlayerId = playerList[nextIndex]
        
        console.log("InternetGameManager: 🔄 Next index: $nextIndex, Next player: $nextPlayerId")
        console.log("InternetGameManager: 🔄 Turn order: ${playerList.mapIndexed { i, id -> "$i:$id" }}")
        
        // Reset selected pin index and hand score for the new turn
        selectedPinIndex = 0
        lastPinScore = 0
        currentHandScore = 0
        
        // Clear last move position and move stack since turn is changing
        lastMovePosition = null
        moveStack.clear()
        blackenedJokers.clear()  // Clear joker tracking for new turn
        console.log("InternetGameManager: 📚 Cleared move stack and blackened jokers for new turn")
        
        // Use atomic update to deal hand AND pass turn together
        // This prevents race conditions where meta updates before hand
        console.log("InternetGameManager: 📤 Atomic turn pass to $nextPlayerId")
        
        SharedBoardManager.passTurnWithNewHand(
            nextPlayerId = nextPlayerId,
            nextPlayerIndex = nextIndex,
            newHandCount = meta.handCount + 1
        ) { success, error ->
            if (success) {
                console.log("InternetGameManager: ✅ Atomic turn pass successful to $nextPlayerId")
                onGamePhaseChanged?.invoke("PLAYING")
                
                // CRITICAL FIX: Explicitly check for AI turn here.
                // Pass "PLAYING" as forcePhase because we just set it in Firebase.
                handleAITurnIfNeeded(nextPlayerId, forcePhase = "PLAYING")
            } else {
                console.error("InternetGameManager: ❌ Atomic turn pass failed: $error")
            }
        }
    }
    
    /**
     * Undo the last move (only during hand pause or your turn).
     * Pops from the move stack to support multiple undos.
     */
    fun undoLastMove(): Boolean {
        console.log("InternetGameManager: 🔄🔄🔄 undoLastMove called 🔄🔄🔄")
        val meta = gameMeta ?: return false
        val playerId = SharedBoardManager.getCurrentPlayerId() ?: return false
        
        // Check if there's anything to undo
        if (moveStack.isEmpty()) {
            console.log("InternetGameManager: No moves to undo (stack empty)")
            return false
        }
        
        // Can undo during pause phase OR during your turn
        val isMyTurn = meta.currentPlayerId == playerId
        val isPaused = meta.gamePhase == "PAUSED"
        
        if (!isPaused && !isMyTurn) {
            console.log("InternetGameManager: Can only undo during pause or your turn")
            return false
        }
        
        // Pop the last move from the stack
        val lastMove = moveStack.removeLast()
        val lastPos = lastMove.position
        
        // Can only undo our own move
        val pin = boardPins[lastPos]
        if (pin?.playerId != playerId) {
            console.log("InternetGameManager: Can only undo own move")
            // Put the move back on the stack
            moveStack.add(lastMove)
            return false
        }
        
        console.log("InternetGameManager: 🔄 Undoing move at $lastPos, pin color: ${pin.color}, stack size now: ${moveStack.size}")
        
        // Parse position
        val parts = lastPos.split("_")
        val row = parts[0].toIntOrNull() ?: run {
            moveStack.add(lastMove)
            return false
        }
        val col = parts[1].toIntOrNull() ?: run {
            moveStack.add(lastMove)
            return false
        }
        
        // Use the color from the move stack (handles jokers correctly)
        val pinColorToRestore = lastMove.pinColor
        
        console.log("InternetGameManager: Restoring color $pinColorToRestore to hand (isJoker=${lastMove.isJoker})")
        
        // Remove the pin (pass current undos used from meta)
        val currentUndosUsed = meta.undosUsedThisHand
        
        // First, undo any pin reversals caused by this move
        if (lastMove.reversedPositions.isNotEmpty()) {
            console.log("InternetGameManager: 🔄 Undoing ${lastMove.reversedPositions.size} pin reversals")
            for (posKey in lastMove.reversedPositions) {
                val parts2 = posKey.split("_")
                val r = parts2[0].toIntOrNull() ?: continue
                val c = parts2[1].toIntOrNull() ?: continue
                // Restore original color from the tracked map
                val originalColor = lastMove.reversedOriginalColors[posKey] ?: "RED"
                SharedBoardManager.updatePin(r, c, mapOf(
                    "isReversed" to false,
                    "color" to originalColor
                )) { success, _ ->
                    if (success) {
                        console.log("InternetGameManager: ✅ Unreversed pin at $posKey, restored color to $originalColor")
                    }
                }
            }
        }
        
        // CRITICAL: Calculate score to subtract BEFORE removing the pin
        // We need to know how many points this move was worth to reverse it
        val boardForScoring = buildBoardFromPins()
        val moveForScoring = Move(
            playerId, 
            if (lastMove.isJoker) Pin.joker(PinColor.BLACK, PinColor.RED) else Pin.regular(PinColor.RED), // Color doesn't affect score calculation logic, just geometry
            Position.fromLegacy(row, col)
        )
        val scoreResult = scoreCalculator.calculateMoveScore(boardForScoring, moveForScoring)
        val pointsToSubtract = scoreResult.points
        
        console.log("InternetGameManager: 🔄 Calling SharedBoardManager.removePin for position ($row, $col), currentUndosUsed=$currentUndosUsed")
        
        SharedBoardManager.removePin(row, col, currentUndosUsed) { success, error ->
            console.log("InternetGameManager: 🔄 removePin callback - success=$success, error=$error")
            if (success) {
                console.log("InternetGameManager: ✅ Pin removed from board at ($row, $col)")
                
                // Consume undo credit
                val creditConsumed = RewardedAdManager.consumeUndoReward()
                console.log("InternetGameManager: 🎫 Undo credit consumed: $creditConsumed")
                
                // Add pin back to hand with the correct color
                val player = players[playerId]
                if (player != null) {
                    val newHand = player.hand.toMutableList()
                    newHand.add(pinColorToRestore)
                    console.log("InternetGameManager: Adding $pinColorToRestore back to hand: $newHand")
                    
                    // Create JS array for Firebase compatibility
                    val handArray = js("[]")
                    newHand.forEachIndexed { i, c -> handArray[i] = c }
                    
                    // Subtract score
                    val newScore = (player.score - pointsToSubtract).coerceAtLeast(0)
                    console.log("InternetGameManager: 📉 Reverting score: ${player.score} - $pointsToSubtract = $newScore")
                    
                    // If this was a joker move, restore the joker count
                    if (lastMove.isJoker) {
                        val newJokersUsed = (player.jokersUsed - 1).coerceAtLeast(0)
                        console.log("InternetGameManager: 🃏 Restoring joker: ${player.jokersUsed} -> $newJokersUsed")
                        SharedBoardManager.updatePlayer(playerId, mapOf(
                            "hand" to handArray,
                            "jokersUsed" to newJokersUsed,
                            "score" to newScore
                        ))
                    } else {
                        SharedBoardManager.updatePlayer(playerId, mapOf(
                            "hand" to handArray,
                            "score" to newScore
                        ))
                    }
                    
                    // Update hand score in meta
                    // We need to fetch the previous move's score if possible, or just reset to 0 if stack empty
                    // Ideally we'd store per-move scores in the stack, but for now just subtracting is safe
                    val newHandScore = (gameMeta?.currentHandScore ?: 0) - pointsToSubtract
                    
                    // Increment undos used counter AND update score meta
                    SharedBoardManager.updateMeta(mapOf(
                        "undosUsedThisHand" to (currentUndosUsed + 1),
                        "currentHandScore" to newHandScore.coerceAtLeast(0),
                        "lastPinScore" to 0, // Reset last pin score since we undid it
                        "scoringPositions" to emptyArray<String>() // Clear highlights
                    ))
                }
                
                // Cancel the hand completion pause if active (this was the 4th pin)
                cancelHandCompletionPause()
                
                // Restore any jokers that were turned black when the hand completed
                // BUT exclude the pin we just removed (lastPos)
                restoreBlackenedJokers(excludePosition = lastPos)
                
                // Update lastMovePosition to the previous move (if any)
                lastMovePosition = moveStack.lastOrNull()?.position
                
                // Clear scoring positions locally too
                scoringPositions = emptySet()
                
                // Notify UI to update
                onBoardUpdated?.invoke()
            } else {
                console.error("InternetGameManager: Failed to undo: $error")
                // Put the move back on the stack since undo failed
                moveStack.add(lastMove)
                onError?.invoke(error ?: "Failed to undo")
            }
        }
        
        return true
    }
    
    /**
     * Force the turn to advance (used for debug/sync button).
     * CRITICAL: Only host should succeed.
     */
    fun forceAdvanceTurn() {
        console.log("InternetGameManager: 🛠️ forceAdvanceTurn() called")
        if (!SharedBoardManager.isCurrentPlayerHost()) {
            console.error("InternetGameManager: ❌ Only the HOST can force turn advance")
            return
        }
        actuallyPassTurnToNextPlayer()
    }
    
    /**
     * Select a pin from the hand.
     * Only works when it's your turn.
     */
    fun selectPin(index: Int) {
        // Only allow selection when it's your turn
        if (!isMyTurn()) {
            console.log("InternetGameManager.selectPin: Not your turn, ignoring")
            return
        }
        
        val meta = gameMeta ?: return
        val currentTurnPlayerId = meta.currentPlayerId
        val player = players[currentTurnPlayerId] ?: return
        
        console.log("InternetGameManager.selectPin: index=$index, hand size=${player.hand.size}")
        
        if (index in player.hand.indices) {
            selectedPinIndex = index
            console.log("InternetGameManager.selectPin: Selected pin $index")
            onBoardUpdated?.invoke()
        } else {
            console.log("InternetGameManager.selectPin: Index $index out of range")
        }
    }
    
    // ========================================
    // Hand and Turn Management
    // ========================================
    
    private fun handleHandComplete(playerId: String) {
        console.log("InternetGameManager: Hand complete for $playerId")
        
        // Set game to paused for 3 second undo window
        SharedBoardManager.updateMeta(mapOf("gamePhase" to "PAUSED"))
    }
    
    private fun handleHandPause() {
        val meta = gameMeta ?: return
        val myPlayerId = SharedBoardManager.getCurrentPlayerId() ?: return
        val isHost = SharedBoardManager.isCurrentPlayerHost()
        
        console.log("InternetGameManager: 🕐 handleHandPause called")
        console.log("InternetGameManager: 🕐 - currentPlayer=${meta.currentPlayerId}")
        console.log("InternetGameManager: 🕐 - me=$myPlayerId, isHost=$isHost")
        console.log("InternetGameManager: 🕐 - handPauseTimeoutId=$handPauseTimeoutId")
        console.log("InternetGameManager: 🕐 - pauseStartedBy=${meta.pauseStartedBy}")
        
        // If we already have a timer running, don't set another one
        if (handPauseTimeoutId != null) {
            console.log("InternetGameManager: 🕐 SKIP - Timer already running (ID=$handPauseTimeoutId)")
            return
        }

        // If WE started the pause (we're the current player who just finished our hand), 
        // we normally have a timer running from startHandCompletionPause.
        // If we don't (e.g. after a refresh), we fall through to handle it below if we are HOST.
        if (meta.pauseStartedBy == myPlayerId && !isHost) {
            console.log("InternetGameManager: 🕐 SKIP - I started this pause and I am CLIENT")
            return
        }
        
        // Set local pause flag for UI purposes
        isInHandPause = true
        
        // Only HOST should handle turn advances
        if (!isHost) {
            console.log("InternetGameManager: 🕐 I am CLIENT - waiting for HOST to handle turn advance")
            return
        }
        
        // HOST: Another player finished their hand and set PAUSED
        // We need to set a fallback timer to advance the turn
        val pauseEndTime = meta.pauseEndTime
        
        if (pauseEndTime != null) {
            val now = Date.now()
            val timeRemaining = pauseEndTime.toDouble() - now
            
            console.log("InternetGameManager: 🏠 HOST observing pause started by ${meta.pauseStartedBy}")
            console.log("InternetGameManager: 🏠 pauseEndTime=$pauseEndTime, now=$now, timeRemaining=${timeRemaining}ms")
            
            if (timeRemaining <= 0) {
                // Pause has already expired!
                console.log("InternetGameManager: 🏠 HOST: Pause expired! Advancing turn immediately.")
                isInHandPause = false
                actuallyPassTurnToNextPlayer()
            } else {
                // Set a timer to advance turn when it expires (add 100ms buffer)
                val timerDelay = (timeRemaining + 100).toInt()
                console.log("InternetGameManager: 🏠 HOST: Setting FALLBACK timer for ${timerDelay}ms")
                handPauseTimeoutId = window.setTimeout({
                    console.log("InternetGameManager: 🏠⏰ HOST FALLBACK TIMER FIRED!")
                    console.log("InternetGameManager: 🏠⏰ Current phase: ${gameMeta?.gamePhase}")
                    isInHandPause = false
                    handPauseTimeoutId = null
                    
                    // Double-check phase is still PAUSED before advancing
                    if (gameMeta?.gamePhase == "PAUSED") {
                        console.log("InternetGameManager: 🏠⏰ Phase still PAUSED - advancing turn")
                        actuallyPassTurnToNextPlayer()
                    } else {
                        console.log("InternetGameManager: 🏠⏰ Phase is ${gameMeta?.gamePhase}, skipping turn advance")
                    }
                }, timerDelay)
                console.log("InternetGameManager: 🏠 HOST: Fallback timer ID: $handPauseTimeoutId")
            }
        } else {
            console.log("InternetGameManager: 🕐 No pauseEndTime set - cannot set fallback timer")
            // Fallback: if no pauseEndTime, just wait 3 seconds
            console.log("InternetGameManager: 🏠 HOST: No pauseEndTime, using default 3 second timer")
            handPauseTimeoutId = window.setTimeout({
                console.log("InternetGameManager: 🏠⏰ HOST DEFAULT TIMER FIRED!")
                isInHandPause = false
                handPauseTimeoutId = null
                if (gameMeta?.gamePhase == "PAUSED") {
                    actuallyPassTurnToNextPlayer()
                }
            }, 3100)
        }
    }
    
    // ========================================
    // Scoring
    // ========================================
    
    private fun updateScoreAfterMove(playerId: String, row: Int, col: Int, pin: Pin) {
        // Build a Board object from the shared pins for score calculation
        val board = buildBoardFromPins()
        val position = Position.fromLegacy(row, col)
        
        // Calculate score for this move using ScoreCalculator
        val move = Move(playerId, pin, position)
        val scoreResult = scoreCalculator.calculateMoveScore(board, move)
        
        // Track pin score (even if 0)
        lastPinScore = scoreResult.points
        currentHandScore += scoreResult.points
        
        // Track scoring positions for white ring highlights
        // Use the new scoringPositions field from ScoreResult
        val newScoringPositions = mutableSetOf<String>()
        for (pos in scoreResult.scoringPositions) {
            val (r, c) = pos.toLegacy()
            newScoringPositions.add("${r}_${c}")
        }
        scoringPositions = newScoringPositions
        
        console.log("InternetGameManager: Pin score: $lastPinScore, Hand score: $currentHandScore, Scoring positions: ${scoringPositions.size}")
        
        // Sound is played via onMetaChanged when lastPinScore is synced to Firebase
        // This ensures all clients hear the same sound
        
        // Sync scores and scoring positions to Firebase so all players can see them
        // Serialize to comma-separated string for robust cross-platform syncing
        val scoringPosString = scoringPositions.joinToString(",")
        
        SharedBoardManager.updateMeta(mapOf(
            "lastPinScore" to lastPinScore,
            "currentHandScore" to currentHandScore,
            "scoringPositions" to scoringPosString
        ))
        
        if (scoreResult.points > 0) {
            val player = players[playerId] ?: return
            val newScore = player.score + scoreResult.points
            
            console.log("InternetGameManager: Score for move: ${scoreResult.points}, new total: $newScore")
            
            SharedBoardManager.updatePlayer(playerId, mapOf("score" to newScore))
            
            // Handle pin reversals if any lines were made (6+ in a row)
            if (scoreResult.pinsToReverse.isNotEmpty()) {
                // Play reversal sound
                if (SettingsManager.isSoundEnabled()) {
                    SoundManager.playPinsReversed()
                }
                handleLineReversals(scoreResult.pinsToReverse)
            }
        }
    }
    
    private fun handleLineReversals(positions: List<Position>) {
        console.log("InternetGameManager: Reversing ${positions.size} pins")
        
        // Track which pins we're reversing for undo
        val reversedKeys = mutableListOf<String>()
        // Track original colors for undo restoration
        val reversedOriginalColors = mutableMapOf<String, String>()
        
        // For each position in the line, reverse the pin
        for (position in positions) {
            val (row, col) = position.toLegacy()
            val posKey = "${row}_${col}"
            val pin = boardPins[posKey]
            
            if (pin == null) {
                // If it's the pin we just placed, we know it needs reversing
                val lastMove = moveStack.lastOrNull()
                if (lastMove != null && lastMove.position == posKey) {
                    console.log("InternetGameManager: Reversing newly placed pin at $posKey to BLACK")
                    reversedKeys.add(posKey)
                    reversedOriginalColors[posKey] = lastMove.pinColor
                    SharedBoardManager.updatePin(row, col, mapOf(
                        "isReversed" to true,
                        "color" to "BLACK"
                    )) { success, error ->
                        if (success) {
                            console.log("InternetGameManager: ✅ Newly placed pin at $posKey reversed to BLACK")
                        } else {
                            console.error("InternetGameManager: ❌ Failed to reverse newly placed pin at $posKey: $error")
                        }
                    }
                    continue
                } else {
                    console.log("InternetGameManager: No pin found at $posKey")
                    continue
                }
            }
            
            console.log("InternetGameManager: Pin at $posKey - color=${pin.color}, isDress=${pin.isDress}, isReversed=${pin.isReversed}")
            
            if (!pin.isReversed) {
                console.log("InternetGameManager: Reversing pin at $posKey to BLACK (was ${pin.color})")
                reversedKeys.add(posKey)
                // Store original color for undo restoration
                reversedOriginalColors[posKey] = pin.color
                // Update both isReversed flag AND color to BLACK
                SharedBoardManager.updatePin(row, col, mapOf(
                    "isReversed" to true,
                    "color" to "BLACK"
                )) { success, error ->
                    if (success) {
                        console.log("InternetGameManager: ✅ Pin at $posKey reversed to BLACK")
                    } else {
                        console.error("InternetGameManager: ❌ Failed to reverse pin at $posKey: $error")
                    }
                }
            } else {
                console.log("InternetGameManager: Pin at $posKey already reversed")
            }
        }
        
        // Store for undo - update the last move in the stack
        lastMoveReversedPositions = reversedKeys
        if (moveStack.isNotEmpty()) {
            val lastMove = moveStack.removeLast()
            moveStack.add(lastMove.copy(
                reversedPositions = reversedKeys,
                reversedOriginalColors = reversedOriginalColors
            ))
            console.log("InternetGameManager: 📚 Updated last move in stack with ${reversedKeys.size} reversed pins and their original colors")
        }
        console.log("InternetGameManager: Tracked ${reversedKeys.size} reversed pins for undo")
    }
    
    // ========================================
    // Game End
    // ========================================
    
    private fun checkForGameEnd() {
        // Game ends when board is full (100 pins)
        if (boardPins.size >= 100) {
            console.log("InternetGameManager: Board full - game ended")
            
            SharedBoardManager.updateMeta(mapOf("gamePhase" to "FINISHED"))
            
            // Calculate final scores
            val finalScores = players.mapValues { it.value.score }
            val winnerId = finalScores.maxByOrNull { it.value }?.key
            
            onGameEnded?.invoke(finalScores, winnerId)
        }
    }
    
    // ========================================
    // Getters for UI
    // ========================================
    
    /**
     * Get the current board as a map of position -> pin.
     */
    fun getBoardPins(): Map<String, SharedBoardManager.SharedPin> = boardPins
    
    /**
     * Get the current game metadata.
     */
    fun getGameMeta(): SharedBoardManager.GameMeta? = gameMeta
    
    /**
     * Get all players.
     */
    fun getPlayers(): Map<String, SharedBoardManager.SharedPlayer> = players
    
    /**
     * Get the current player's hand (the player whose turn it is).
     * All players see the same hand - the hand of whoever is currently playing.
     */
    fun getCurrentHand(): List<String> {
        val meta = gameMeta
        if (meta == null) {
            console.log("InternetGameManager.getCurrentHand: meta is null!")
            return emptyList()
        }
        val currentTurnPlayerId = meta.currentPlayerId
        console.log("InternetGameManager.getCurrentHand: looking for player $currentTurnPlayerId")
        console.log("InternetGameManager.getCurrentHand: available players: ${players.keys}")
        
        val player = players[currentTurnPlayerId]
        if (player == null) {
            console.log("InternetGameManager.getCurrentHand: player $currentTurnPlayerId not found in players map!")
            return emptyList()
        }
        
        val hand = player.hand
        console.log("InternetGameManager.getCurrentHand: currentTurnPlayer=$currentTurnPlayerId, hand=$hand")
        return hand
    }
    
    /**
     * Get the selected pin index.
     */
    fun getSelectedPinIndex(): Int = selectedPinIndex
    
    /**
     * Get the last pin score (score from most recent pin placed).
     * Reads from Firebase meta so all players see the same value.
     */
    fun getLastPinScore(): Int = gameMeta?.lastPinScore ?: 0
    
    /**
     * Get the current hand score (accumulated score for current hand).
     * Reads from Firebase meta so all players see the same value.
     */
    fun getCurrentHandScore(): Int = gameMeta?.currentHandScore ?: 0
    
    /**
     * Get the scoring positions (pins that contributed to the last score).
     * Used for rendering white ring highlights.
     */
    fun getScoringPositions(): List<String> = gameMeta?.scoringPositions ?: emptyList()
    
    /**
     * Get the number of jokers remaining for the current turn player.
     * Returns 4 - jokersUsed (max 4 jokers per game).
     */
    fun getJokersRemaining(): Int {
        val meta = gameMeta ?: return 4
        val currentTurnPlayerId = meta.currentPlayerId
        val player = players[currentTurnPlayerId] ?: return 4
        return 4 - player.jokersUsed
    }
    
    /**
     * Check if the current turn player can use a joker.
     */
    fun canUseJoker(): Boolean {
        val meta = gameMeta ?: return false
        val currentTurnPlayerId = meta.currentPlayerId
        val player = players[currentTurnPlayerId] ?: return false
        return player.jokersUsed < 4
    }
    
    /**
     * Check if it's the local player's turn.
     */
    fun isMyTurn(): Boolean {
        val localPlayerId = SharedBoardManager.getCurrentPlayerId()
        val currentTurnPlayerId = gameMeta?.currentPlayerId
        val result = localPlayerId != null && localPlayerId == currentTurnPlayerId
        // console.log("InternetGameManager.isMyTurn: localPlayer=$localPlayerId, currentTurn=$currentTurnPlayerId, result=$result")
        return result
    }

    /**
     * Get the player record for the local user.
     */
    fun getCurrentPlayer(): SharedBoardManager.SharedPlayer? {
        val myId = SharedBoardManager.getCurrentPlayerId() ?: return null
        return players[myId]
    }
    
    /**
     * Check if the game is paused.
     */
    fun isPaused(): Boolean = gameMeta?.gamePhase == "PAUSED"
    
    /**
     * Check if we're in the hand completion pause (3-second window).
     */
    fun isInHandCompletionPause(): Boolean = isInHandPause
    
    /**
     * Check if undo is available.
     * Undo is available when:
     * 1. It's your turn (or game is paused)
     * 2. You have placed a pin this turn (moveStack is not empty)
     * 3. The pin at that position belongs to you
     * 4. You have undo credits available
     */
    fun canUndo(): Boolean {
        val playerId = SharedBoardManager.getCurrentPlayerId() ?: return false
        val meta = gameMeta ?: return false
        
        // Check if there's anything to undo
        if (moveStack.isEmpty()) {
            console.log("canUndo: false - move stack is empty")
            return false
        }
        
        val lastMove = moveStack.last()
        val lastPos = lastMove.position
        
        // Can undo if it's your turn OR game is paused
        val isMyTurn = meta.currentPlayerId == playerId
        val isPaused = meta.gamePhase == "PAUSED"
        
        if (!isMyTurn && !isPaused) {
            console.log("canUndo: false - not my turn and not paused (currentPlayer=${meta.currentPlayerId}, me=$playerId)")
            return false
        }
        
        // Check if we have undo credits available
        val hasCredits = RewardedAdManager.hasUndoCredits()
        if (!hasCredits) {
            console.log("canUndo: false - no undo credits available")
            return false
        }
        
        // Check if the last move was ours
        val pin = boardPins[lastPos]
        val canUndo = pin?.playerId == playerId
        console.log("canUndo: $canUndo - lastPos=$lastPos, pinOwner=${pin?.playerId}, me=$playerId, hasCredits=$hasCredits, stackSize=${moveStack.size}")
        return canUndo
    }
    
    /**
     * Build a Board from a map of SharedPins (used for fresh Firebase data).
     */
    private fun buildBoardFromPinsMap(boardPinsMap: Map<String, SharedBoardManager.SharedPin>): Board {
        console.log("InternetGameManager: 🔍 buildBoardFromPinsMap - map has ${boardPinsMap.size} entries")
        var board = Board(initializeDressPins = false)
        
        for ((posKey, sharedPin) in boardPinsMap) {
            val parts = posKey.split("_")
            val row = parts[0].toIntOrNull() ?: continue
            val col = parts[1].toIntOrNull() ?: continue
            
            val position = Position.fromLegacy(row, col)
            val color = try { PinColor.valueOf(sharedPin.color) } catch (_: Exception) { continue }
            
            val pin = if (sharedPin.isJoker && sharedPin.originalColor != null) {
                // For jokers in SharedPin:
                // - color = the displayed color (what it was played as, effective for scoring)
                // - originalColor = the color it counts as for scoring (same as color)
                // - handColor = the original hand color (what was sacrificed)
                // 
                // Pin.joker(originalColor, playedAsColor) expects:
                // - originalColor = the hand color that was sacrificed
                // - playedAsColor = the effective color for scoring (displayed color)
                val handCol = try { 
                    PinColor.valueOf(sharedPin.handColor ?: sharedPin.originalColor) 
                } catch (_: Exception) { color }
                val playedAsCol = color // Use the displayed color as the effective scoring color
                Pin.joker(handCol, playedAsCol)
            } else if (sharedPin.isDress) {
                Pin.dress(color)
            } else {
                Pin.regular(color)
            }.let { p ->
                if (sharedPin.isReversed) p.reversed() else p
            }
            
            if (board.isEmpty(position)) {
                board = board.placePinAt(position, pin)
            }
        }
        
        console.log("InternetGameManager: 🔍 buildBoardFromPinsMap - final board has ${board.getOccupiedPositions().size} pins")
        return board
    }
    
    /**
     * Build a Board object from the shared pins (for score calculation).
     */
    fun buildBoardFromPins(): Board {
        var board = Board(initializeDressPins = false)
        
        for ((posKey, sharedPin) in boardPins) {
            val parts = posKey.split("_")
            val row = parts[0].toIntOrNull() ?: continue
            val col = parts[1].toIntOrNull() ?: continue
            
            val position = Position.fromLegacy(row, col)
            val color = try { PinColor.valueOf(sharedPin.color) } catch (_: Exception) { continue }
            
            val pin = if (sharedPin.isJoker && sharedPin.originalColor != null) {
                // For jokers in SharedPin:
                // - color = the displayed color (what it was played as, effective for scoring)
                // - originalColor = the color it counts as for scoring (same as color)
                // - handColor = the original hand color (what was sacrificed)
                // 
                // Pin.joker(originalColor, playedAsColor) expects:
                // - originalColor = the hand color that was sacrificed
                // - playedAsColor = the effective color for scoring (displayed color)
                val handCol = try { 
                    PinColor.valueOf(sharedPin.handColor ?: sharedPin.originalColor) 
                } catch (_: Exception) { color }
                val playedAsCol = color // Use the displayed color as the effective scoring color
                Pin.joker(handCol, playedAsCol)
            } else if (sharedPin.isDress) {
                Pin.dress(color)
            } else {
                Pin.regular(color)
            }.let { p ->
                if (sharedPin.isReversed) p.reversed() else p
            }
            
            if (board.isEmpty(position)) {
                board = board.placePinAt(position, pin)
            }
        }
        
        return board
    }
    
    // ========================================
    // AI Player Management
    // ========================================
    
    /**
     * Check if a player is an AI player and handle their turn.
     * This is called whenever the turn changes or the state is polled.
     */
    private fun handleAITurnIfNeeded(currentPlayerId: String, forcePhase: String? = null) {
        // CRITICAL FIX: Only the Host should execute AI moves.
        if (!SharedBoardManager.isCurrentPlayerHost()) {
            return
        }

        if (isAIMoveScheduled) {
            return
        }

        val effectivePhase = forcePhase ?: gameMeta?.gamePhase
        if (gameMeta == null || effectivePhase != "PLAYING") {
            console.log("InternetGameManager: 🤖 AI check skipped - effective phase is $effectivePhase")
            return
        }

        val sharedPlayer = players[currentPlayerId]
        val isAI = sharedPlayer?.isAI == true
        console.log("InternetGameManager: 🤖 Checking player $currentPlayerId (Found: ${sharedPlayer != null}, isAI: $isAI)")
        
        if (isAI && sharedPlayer != null) {
            val difficulty = sharedPlayer.aiDifficulty ?: "EASY"
            console.log("InternetGameManager: 🤖 AI player $currentPlayerId (${sharedPlayer.name}) turn detected - Difficulty: $difficulty")
            
            // Create AIPlayer instance for this turn
            val aiPlayer = AIPlayer(currentPlayerId, sharedPlayer.name, difficulty)
            console.log("InternetGameManager: 🤖 AIPlayer strategy: $difficulty (Logic v${ExpertStrategy.VERSION})")
            
            // Add a small delay to make AI moves feel more natural
            isAIMoveScheduled = true
            window.setTimeout({
                isAIMoveScheduled = false
                executeAIMove(aiPlayer, currentPlayerId)
            }, 1500) // 1.5 second delay
        } else {
            console.log("InternetGameManager: Player $currentPlayerId is human")
        }
    }
    
    /**
     * Execute an AI move for the given AI player.
     * CRITICAL: Fetches fresh board state from Firebase to avoid stale data.
     */
    private fun executeAIMove(aiPlayer: AIPlayer, playerId: String) {
        console.log("InternetGameManager: 🤖 Executing AI move for ${aiPlayer.name} (v${ExpertStrategy.VERSION})")
        
        val meta = gameMeta
        val currentPlayer = players[playerId]
        
        if (meta == null || currentPlayer == null) {
            console.error("InternetGameManager: 🤖 Cannot execute AI move - missing game state")
            return
        }
        
        // Check if game is paused
        if (meta.gamePhase == "PAUSED") {
            console.log("InternetGameManager: 🤖 Game is paused, AI waiting")
            return
        }
        
        // Check if it's still this AI's turn (in case turn changed while waiting)
        if (meta.currentPlayerId != playerId) {
            console.log("InternetGameManager: 🤖 Turn changed, canceling AI move")
            return
        }
        
        try {
            // CRITICAL FIX: Fetch fresh board state from Firebase before AI evaluation
            // This prevents race conditions where boardPins is stale
            console.log("InternetGameManager: 🤖 Fetching fresh board state from Firebase...")
            SharedBoardManager.fetchBoardOnce { freshBoardPins ->
                console.log("InternetGameManager: 🤖 Fresh board: ${freshBoardPins.size} pins (cached had ${boardPins.size})")
                
                // Build board from fresh Firebase data
                val board = buildBoardFromPinsMap(freshBoardPins)
                val availablePins = convertHandToPins(currentPlayer.hand)
                
                if (availablePins.isEmpty()) {
                    console.log("InternetGameManager: 🤖 AI has no pins in hand")
                    return@fetchBoardOnce
                }
                
                // Create a basic GameState for the AI
                val gameState = createGameStateForAI(board, playerId)
                
                // Create BoardAnalyzer for AI strategy
                val boardAnalyzer = BoardAnalyzer()
                
                // Generate AI move
                val move = aiPlayer.generateMove(gameState, availablePins, boardAnalyzer)
                
                if (move != null) {
                    console.log("InternetGameManager: 🤖 AI generated move: ${move.pin.color} at ${move.position}")
                    console.log("InternetGameManager: 🤖 Move pin details: isJoker=${move.pin.isJoker}, color=${move.pin.color.name}, originalColor=${move.pin.originalColor?.name}")
                    console.log("InternetGameManager: 🤖 Current hand: ${currentPlayer.hand.joinToString()}")
                    console.log("InternetGameManager: 🤖 Looking for pin: ${move.pin.color.name}")
                    
                    // Convert position to row/col
                    val (row, col) = move.position.toLegacy()
                    
                    // Find the pin index in the hand
                    val pinIndex = findPinIndexInHand(currentPlayer.hand, move.pin)
                    if (pinIndex >= 0) {
                        console.log("InternetGameManager: 🤖 Found pin at index $pinIndex")
                        // Set the selected pin index for the AI
                        selectedPinIndex = pinIndex
                        
                        // Execute the move - use makeMoveWithJoker for joker moves
                        if (move.pin.isJoker) {
                            console.log("InternetGameManager: 🤖 Executing JOKER move: ${move.pin.originalColor?.name} → ${move.pin.color.name}")
                            makeMoveWithJoker(row, col, move.pin.color.name, playerId)
                        } else {
                            console.log("InternetGameManager: 🤖 Executing NORMAL move")
                            makeMove(row, col, playerId)
                        }
                        
                        // CRITICAL: Wait for Firebase to sync before next AI move
                        // This ensures the AI sees its previous pins when evaluating the next move
                        window.setTimeout({
                            console.log("InternetGameManager: 🤖 Firebase sync delay complete, ready for next move")
                        }, 500)
                    } else {
                        console.error("InternetGameManager: 🤖 Could not find pin in AI hand")
                        console.error("InternetGameManager: 🤖 Hand contents: [${currentPlayer.hand.joinToString()}]")
                        console.error("InternetGameManager: 🤖 Target pin: ${move.pin.color.name}")
                        
                        // Fallback: try to find any valid pin in the hand and make a random move
                        if (currentPlayer.hand.isNotEmpty()) {
                            console.log("InternetGameManager: 🤖 Attempting fallback with first available pin")
                            selectedPinIndex = 0
                            
                            // Find an empty position for fallback move
                            val emptyPositions = getEmptyPositions()
                            if (emptyPositions.isNotEmpty()) {
                                val fallbackPos = emptyPositions.random()
                                val (fallbackRow, fallbackCol) = fallbackPos.toLegacy()
                                console.log("InternetGameManager: 🤖 Fallback move: ${currentPlayer.hand[0]} at ($fallbackRow, $fallbackCol)")
                                makeMove(fallbackRow, fallbackCol, playerId)
                            }
                        }
                    }
                } else {
                    console.log("InternetGameManager: 🤖 AI could not generate a valid move")
                }
            }
        } catch (e: Exception) {
            console.error("InternetGameManager: 🤖 Error executing AI move: ${e.message}")
        }
    }
    
    /**
     * Convert a hand (list of color names) to a list of Pin objects.
     */
    private fun convertHandToPins(hand: List<String>): List<Pin> {
        return hand.mapNotNull { colorName ->
            try {
                val color = PinColor.valueOf(colorName)
                Pin.regular(color)
            } catch (e: Exception) {
                console.error("InternetGameManager: Invalid color in hand: $colorName")
                null
            }
        }
    }
    
    /**
     * Get all empty positions on the current board.
     */
    private fun getEmptyPositions(): List<Position> {
        val empty = mutableListOf<Position>()
        val board = buildBoardFromPins()
        
        // Iterate through all positions using legacy coordinates (0-9 each)
        for (row in 0 until 10) {
            for (col in 0 until 10) {
                val position = Position.fromLegacy(row, col)
                if (board.isEmpty(position)) {
                    empty.add(position)
                }
            }
        }
        return empty
    }

    /**
     * Find the index of a pin in the hand that matches the given pin.
     * For jokers, looks for the original pin color being sacrificed.
     */
    private fun findPinIndexInHand(hand: List<String>, targetPin: Pin): Int {
        // For jokers, we need to find the original pin being sacrificed
        val targetColor = if (targetPin.isJoker) {
            targetPin.originalColor?.name ?: targetPin.color.name
        } else {
            targetPin.color.name
        }
        
        return hand.indexOfFirst { it == targetColor }
    }
    
    /**
     * Create a basic GameState for AI decision making.
     */
    private fun createGameStateForAI(board: Board, currentPlayerId: String): GameState {
        // Convert SharedPlayer map to Player list - use map keys as player IDs
        val playerList = players.entries.map { (playerId, sharedPlayer) ->
            Player(
                id = playerId,
                name = sharedPlayer.name,
                score = sharedPlayer.score,
                jokersUsed = sharedPlayer.jokersUsed,
                isAI = sharedPlayer.isAI
            )
        }
        
        val currentPlayerIndex = playerList.indexOfFirst { it.id == currentPlayerId }.coerceAtLeast(0)
        val currentPlayer = playerList[currentPlayerIndex]
        
        // Get current hand for the AI player
        val currentHand = players[currentPlayerId]?.hand?.mapNotNull { colorName ->
            try {
                val color = PinColor.valueOf(colorName)
                Pin.regular(color)
            } catch (e: Exception) {
                null
            }
        } ?: emptyList()
        
        return GameState(
            gameId = "ai_game",
            board = board,
            players = playerList,
            currentPlayerIndex = currentPlayerIndex,
            gamePhase = GamePhase.PLAYING,
            currentHand = currentHand,
            handCount = 1,
            started = true
        )
    }
}
